import React, { createContext, useContext, useState, ReactNode } from "react";
export type SubscriptionPlan = {
    id: number;
    name: string;
    name_en: string;
    project_in_team: number;
    members_in_team: number;
    tasks_in_project: number;
    storage: number;
};

export type WarningState = {
    storage_warning: { show: boolean };
    user_limits_warning: { show: boolean; limit?: number; exist?: number };
    project_limits_warning: { show: boolean; limit: number; exist: number };
    task_limits_warning: { show: boolean; limit: number; exist: number };
    trial_offer: {
        show: boolean;
        subscription_plan: SubscriptionPlan;
        up_to: string;
    };
    subscription_expired: { show: boolean; expired: string };
    trial_subscription_expired: { show: boolean; expired: string };
};

// Default initial value
const defaultWarnings: WarningState = {
    storage_warning: { show: false },
    user_limits_warning: { show: false },
    project_limits_warning: { show: false, limit: 0, exist: 0 },
    task_limits_warning: { show: false, limit: 0, exist: 0 },
    trial_offer: {
        show: false,
        subscription_plan: {
            id: 0,
            name: "",
            name_en: "",
            project_in_team: 0,
            members_in_team: 0,
            tasks_in_project: 0,
            storage: 0,
        },
        up_to: "",
    },
    subscription_expired: { show: false, expired: "" },
    trial_subscription_expired: { show: false, expired: "" },
};

type WarningsContextType = {
    warnings: WarningState;
    setWarnings: React.Dispatch<React.SetStateAction<WarningState>>;
    refetchWarnings: () => void;
    isRefetchWarning: boolean;
};

// Create context
const WarningsContext = createContext<WarningsContextType | undefined>(
    undefined
);

// Provider
export const WarningsProvider = ({ children }: { children: ReactNode }) => {
    const [warnings, setWarnings] = useState<WarningState>(defaultWarnings);
    const [isRefetchWarning, setIsRefetchWarning] = useState<boolean>(false);

    const refetchWarnings = () => {
        setIsRefetchWarning(!isRefetchWarning);
    }
    return (
        <WarningsContext.Provider value={{ warnings, setWarnings, refetchWarnings, isRefetchWarning }}>
            {children}
        </WarningsContext.Provider>
    );
};

// Hook
export const useWarnings = () => {
    const ctx = useContext(WarningsContext);
    if (!ctx) throw new Error("useWarnings must be used within WarningsProvider");
    return ctx;
};
